import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { mergeIssues } from "./feed_issues";
import { FeedIssuesNote } from "./FeedIssuesNote";
import { countryName, humanDate, messageFor, money, pct, personName, stamp, STORE_LABEL } from "./format";
import { LOGS_KEY } from "./logs";
import { signedMoney } from "./OrderChart";
import { PeriodPicker } from "./PeriodPicker";
import { useDebounced } from "@/lib/useDebounced";
import {
  chargeDraft, cleanAmount, clearStandardDelivery, commitExpected, commitRunIds, commitThresholds, committedInWindow, freeTierLine, isBusy, ORDER_TYPES, ordersLinkSearch,
  applyList, replayCandidate, replayLines, replayQuery, replayRefusal, reviewChangedSentence, savedRows, searchForOrderType, searchForShippingPeriod, setStandardDelivery, SHIPPING_COPY as C,
  SHIPPING_KEY, shippingParams, shippingQuery, shippingRefusal, stageThreshold, thresholdPending, unchangedLines, unsigned, unstageThreshold, type CommittedRun,
  type Shipping, type ShippingParams, type ShippingRow, type UnchangedLine, type WriteAction,
} from "./shipping";

const COUNT = new Intl.NumberFormat("en-GB");
const count = (n: number) => COUNT.format(n);

/**
 * The Shipping pane (C1 v1.19, go-live unit 5; C1 v1.21 adds the replay line under New threshold and the free-tier cost). One read: every registry country with its threshold and
 * standard delivery charge beside the period's Shopify order actuals, plus the outside rows. Saving a threshold stages it;
 * one Commit records every saved threshold and hands back the list to type into the Shopify admin (nothing is pushed).
 * Every figure is C2's string; the browser does no arithmetic.
 */
export function ShippingPane({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const go = (next: Record<string, unknown>) => void navigate({ to: ".", search: next, replace: true });
  const [today] = useState(() => new Date());
  const [committed, setCommitted] = useState<CommittedRun[] | null>(null);
  const [applyDone, setApplyDone] = useState(false);
  const params = shippingParams(search);
  const read = useQuery(shippingQuery(params));

  const heading = (
    <div>
      <h2 className="text-lg font-semibold text-ks-ink">{C.title}</h2>
      <p className="text-sm text-ks-grey-1">{C.desc}</p>
    </div>
  );
  if (read.isPending) {
    return <div className="flex flex-col gap-3">{heading}<Card><CardContent className="flex flex-col gap-2 py-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</CardContent></Card></div>;
  }
  const failed = read.error && (
    <Alert role="alert" variant="destructive"><AlertDescription className="flex flex-wrap items-center gap-3">
      {read.data && C.stale} {readSentence(read.error, areaName)}
      <Button size="sm" variant="outline" data-nonmutating onClick={() => void read.refetch()}>Retry</Button>
    </AlertDescription></Alert>
  );
  // With no data the alert is the pane; a refetch that fails over data keeps the table, its typing and any open dialog (review R2-1).
  if (!read.data) return <div className="flex flex-col gap-3">{heading}{failed}</div>;
  const s = read.data;
  const excluded = s.orders - s.costed;
  // After a reload, only runs committed recently are still to apply (review ruling A4); the commit's own answer always shows.
  const applyRuns = applyList(committed, s.last_committed, today);
  return (
    <div className="flex flex-col gap-3">
      {heading}
      {failed}
      <div className="flex flex-wrap items-center gap-3">
        <PeriodPicker from={params.from} to={params.to} book={s.book} today={today} onChange={(from, to) => go(searchForShippingPeriod(search, from, to))} />
        <div role="group" aria-label={C.orderType.label} className="flex gap-1">
          {ORDER_TYPES.map((t) => (
            <Button key={t} size="sm" data-nonmutating variant={params.orders === t ? "default" : "outline"} aria-pressed={params.orders === t}
              onClick={() => go(searchForOrderType(search, t))}>{C.orderType[t]}</Button>
          ))}
        </div>
      </div>
      {/* C1 v1.64 (C2 v1.62): the order feeds' open issues, as the Price Run names its own. */}
      <FeedIssuesNote issues={mergeIssues(s.feed_issues)} />
      {s.untyped > 0 && <Alert role="status"><AlertDescription>{C.untyped(s.untyped)}</AlertDescription></Alert>}
      {s.book.from === null && <Alert role="status"><AlertDescription><b>{C.emptyTitle}.</b> {C.emptyBody}</AlertDescription></Alert>}
      {excluded > 0 && <Alert role="status"><AlertDescription>{C.partial(count(excluded), count(s.orders))}</AlertDescription></Alert>}
      {/* The commit's answer first, then every other run of the last 14 days, so the list to apply survives a reload (D9). */}
      {!applyDone && applyRuns.length > 0 && <ThresholdApplyPanel committed={applyRuns} fresh={committed?.length ?? 0} onDone={() => { setCommitted(null); setApplyDone(true); }} />}
      <ThresholdBatchBar s={s} params={params} queryKey={shippingQuery(params).queryKey} areaName={areaName} onCommitted={(runs) => { setCommitted(runs); setApplyDone(false); }} />
      <Card className="overflow-x-auto">
        <Table className="[&_td]:whitespace-normal [&_th]:whitespace-normal">
          <TableHeader>
            <TableRow>
              <TableHead />
              <TableHead colSpan={2}>{C.group.setup}</TableHead>
              <TableHead colSpan={6}>{s.window.from && s.window.to ? C.group.actuals(humanDate(s.window.from), humanDate(s.window.to), count(s.costed), count(s.orders)) : C.group.actualsNone}</TableHead>
              <TableHead colSpan={1}>{C.group.change}</TableHead>
            </TableRow>
            <TableRow>{C.columns.map((c, i) => <TableHead key={c} className={i >= 3 && i <= 8 ? "text-right" : undefined}>{c}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {s.rows.map((row) => <ShippingRowView key={`${row.store}|${row.country ?? ""}`} row={row} areaName={areaName} inWindow={committedInWindow(row, s.window)} params={params}
              onOrders={() => row.country && go(ordersLinkSearch(search, { country: row.country, store: row.store }, params))} />)}
          </TableBody>
        </Table>
      </Card>
      <p className="text-xs text-ks-grey-1">{C.footnoteOrders}</p>
      <p className="text-xs text-ks-grey-1">{C.footnoteCommit}</p>
      <div className="rounded-md border border-ks-grey-4 bg-ks-grey-5 p-3 text-sm text-ks-ink">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-ks-grey-1">{C.notModelledTag}</div>
        {C.notModelled}
      </div>
    </div>
  );
}

function ShippingRowView({ row, areaName, inWindow, onOrders, params }: { row: ShippingRow; areaName: string; inWindow: boolean; onOrders: () => void; params: ShippingParams }) {
  const cur = row.currency;
  return (
    <TableRow className={row.loses_money ? "bg-ks-warn/5" : undefined}>
      <TableCell>
        <b className="text-ks-ink">{row.country ? countryName(row.country) : C.unknownCountry}</b>
        <div className="text-xs text-ks-grey-1">{row.country ?? "?"} · {cur} · {STORE_LABEL[row.store] ?? row.store}</div>
        {row.outside && <div className="mt-1"><Badge variant="outline" title={C.outsideTitle[row.outside]}>{C.outside[row.outside]}</Badge></div>}
        {row.currency_mixed && <div className="mt-1"><Badge variant="outline" className="border-ks-warn text-ks-warn" title={C.mixedTitle}>{C.mixed}</Badge></div>}
      </TableCell>
      <TableCell><ThresholdCell row={row} inWindow={inWindow} /></TableCell>
      <TableCell>{row.definition ? <DeliveryCell key={row.definition.standard_delivery_charge ?? "none"} row={row} areaName={areaName} /> : <span className="text-ks-grey-1">—</span>}</TableCell>
      <TableCell className="text-right"><OrdersCell row={row} onOrders={onOrders} /></TableCell>
      <TableCell className="text-right tabular-nums">{money(row.net_revenue_ex_vat, cur)}</TableCell>
      <TableCell className="text-right"><ShippingCell row={row} /></TableCell>
      <TableCell className="text-right tabular-nums">{row.cogs == null ? money(null, cur) : `−${money(row.cogs, cur)}`}</TableCell>
      <TableCell className="text-right">
        {row.fulfilment_fee_total == null ? money(null, cur) : <>
          <span className="tabular-nums">−{money(row.fulfilment_fee_total, cur)}</span>
          <div className="text-xs text-ks-grey-1">{C.fees(money(row.pick_pack, cur), money(row.packaging, cur), money(row.courier, cur))}</div>
        </>}
      </TableCell>
      <TableCell className="text-right"><ProfitCell row={row} /></TableCell>
      <TableCell>{row.definition ? <NewThresholdCell row={row} areaName={areaName} params={params} /> : null}</TableCell>
    </TableRow>
  );
}

function ThresholdCell({ row, inWindow }: { row: ShippingRow; inWindow: boolean }) {
  const d = row.definition;
  if (!d) return <span className="text-ks-grey-1">—</span>;
  return (
    <>
      {d.threshold == null ? <Badge variant="secondary">{C.noTier}</Badge> : <span className="tabular-nums">{money(d.threshold, d.currency)}</span>}
      {d.source === "committed_run" && d.committed_at && <>
        <div className="text-xs text-ks-grey-1">{C.committedBy(humanDate(d.committed_at), personName(d.committed_by))}</div>
        {/* Nothing records whether it was typed into Shopify (debt D9), so a committed threshold always reads as to apply by hand. */}
        <div className="text-xs text-ks-grey-1">{C.applyByHand}</div>
      </>}
      {inWindow && <div className="text-xs text-ks-grey-1">{C.committedInPeriod(humanDate(d.effective_date))}</div>}
      {row.free_tier && <div className="text-xs text-ks-grey-1">{freeTierLine(row, money)}</div>}
    </>
  );
}

/** "At X: about N orders move, gains Y": C2's replay of the typed threshold, read as sentences; nothing is computed here. */
function ReplayLine({ country, currency, candidate, current, params, areaName }: { country: string; currency: string; candidate: string; current: string | null; params: ShippingParams; areaName: string }) {
  const q = useQuery(replayQuery(country, candidate, current, params));
  if (q.isPending) return <span className="text-xs text-ks-grey-1">{C.replay.checking}</span>;
  if (q.isError) return <span className="text-xs text-ks-warn">{replayRefusal(q.error, areaName)}</span>;
  const { line, notes } = replayLines(q.data, currency, money);
  return (
    <div className="max-w-xs text-xs text-ks-grey-1" title={C.replay.title}>
      <span className="text-ks-ink">{line}</span>
      {notes.length > 0 && <div>{notes.join("; ")}</div>}
    </div>
  );
}

function OrdersCell({ row, onOrders }: { row: ShippingRow; onOrders: () => void }) {
  const e = row.excluded;
  const parts = (Object.keys(C.excludedPart) as Array<keyof typeof C.excludedPart>).filter((k) => e[k] > 0).map((k) => C.excludedPart[k](count(e[k])));
  const note = row.orders === 0 ? C.noOrders : row.orders === row.costed ? C.allCosted : C.excludedNote(count(row.orders - row.costed), parts.join(", "));
  return (
    <>
      {row.orders > 0 && row.country
        ? <Button variant="link" className="h-auto p-0 tabular-nums" data-nonmutating title={C.ordersLinkTitle} onClick={onOrders}>{count(row.costed)}</Button>
        : <span className="tabular-nums">{count(row.costed)}</span>}
      <span className="text-sm text-ks-grey-1"> / {count(row.orders)}</span>
      <div className="text-xs text-ks-grey-1">{note}</div>
    </>
  );
}

function ShippingCell({ row }: { row: ShippingRow }) {
  const cur = row.currency;
  // The counts and the courier are served even when the shipping sum is not known (an unsplit order): they still show.
  return (
    <>
      {row.shipping_paid_ex_vat == null ? <span className="text-ks-grey-1">{money(null, cur)}</span> : <span className="tabular-nums">{money(row.shipping_paid_ex_vat, cur)}</span>}
      {row.costed > 0 && <div className="text-xs text-ks-grey-1">{C.paidShipping(count(row.paid_shipping_orders))}</div>}
      {row.courier != null && <div className="text-xs text-ks-grey-1">{C.couriersBilled(money(row.courier, cur))}</div>}
      {row.shipping_net != null && row.shipping_net.startsWith("-") && <div className="text-xs text-ks-warn">{C.subsidised(money(unsigned(row.shipping_net), cur))}</div>}
      {row.shipping_partly_refunded > 0 && <div className="text-xs text-ks-grey-1">{C.partlyRefunded(count(row.shipping_partly_refunded))}</div>}
      {row.shipping_unsplit > 0 && <div className="text-xs text-ks-warn">{C.unsplit(count(row.shipping_unsplit))}</div>}
    </>
  );
}

function ProfitCell({ row }: { row: ShippingRow }) {
  if (row.profit == null) return <span className="text-ks-grey-1">{money(null, row.currency)}</span>;
  return (
    <>
      <span className={`font-semibold tabular-nums ${row.loses_money ? "text-ks-warn" : "text-ks-pass"}`}>{signedMoney(row.profit, row.currency)}</span>
      {row.profit_pct != null && <div className="text-xs text-ks-grey-1">{C.ofRevenue(pct(row.profit_pct))}</div>}
      {row.loses_money && <div className="mt-1"><Badge variant="destructive">{C.losesMoney}</Badge></div>}
    </>
  );
}

/** Every shipping write carries this key, so the commit can wait for a save in flight (review ruling 6). */
const WRITE_KEY = ["pricing", "shipping", "write"] as const;
function useShippingWrite<T, R>(fn: (v: T) => Promise<R>, areaName: string, action: WriteAction, onDone?: (data: R) => void) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationKey: WRITE_KEY,
    mutationFn: fn,
    onSuccess: (data: R) => { void client.invalidateQueries({ queryKey: SHIPPING_KEY }); setError(null); onDone?.(data); },
    // A refusal keeps the typed value; the read is refetched so a run committed under the save shows (the pane saves again).
    onError: (e) => { setError(shippingRefusal(e, areaName, action)); void client.invalidateQueries({ queryKey: SHIPPING_KEY }); },
  });
  return { mutation, error, setError };
}

function DeliveryCell({ row, areaName }: { row: ShippingRow; areaName: string }) {
  const d = row.definition!;
  const [editing, setEditing] = useState(false);
  const initial = chargeDraft(row);
  const [value, setValue] = useState(initial);
  const save = useShippingWrite((v: string) => setStandardDelivery(d.country, v), areaName, "save", () => setEditing(false));
  const clear = useShippingWrite((_: null) => clearStandardDelivery(d.country), areaName, "clear", () => { setEditing(false); setValue(""); });
  const submit = () => {
    const v = cleanAmount(value);
    if (!v) { save.setError(C.enterAmount); return; }
    save.mutation.mutate(v);
  };
  if (!editing) {
    return (
      <>
        {d.standard_delivery_charge == null ? <Badge variant="outline" className="border-ks-warn text-ks-warn">{C.notSet}</Badge> : <span className="tabular-nums">{money(d.standard_delivery_charge, d.currency)}</span>}
        {d.standard_delivery_charge != null && d.standard_delivery_set_by && d.standard_delivery_set_at &&
          <div className="text-xs text-ks-grey-1">{C.setBy(personName(d.standard_delivery_set_by), humanDate(d.standard_delivery_set_at))}</div>}
        {d.standard_delivery_charge == null && row.paid_shipping_hint &&
          <div className="text-xs text-ks-grey-1" title={C.hintTitle}>{C.hint(money(row.paid_shipping_hint.amount, d.currency), row.paid_shipping_hint.orders)}</div>}
        <div><Button variant="link" className="h-auto p-0 text-xs" onClick={() => setEditing(true)}>{d.standard_delivery_charge == null ? C.set : C.edit}</Button></div>
      </>
    );
  }
  const busy = save.mutation.isPending || clear.mutation.isPending;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <Input className="h-8 w-24" inputMode="decimal" aria-label={C.chargeLabel(countryName(d.country))} value={value} onChange={(e) => setValue(e.target.value)} />
        <span className="text-xs text-ks-grey-1">{d.currency}</span>
      </div>
      <div className="flex gap-1">
        <Button size="sm" disabled={busy} onClick={submit}>{C.save}</Button>
        {d.standard_delivery_charge != null && <Button size="sm" variant="outline" disabled={busy} onClick={() => clear.mutation.mutate(null)}>{C.clear}</Button>}
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setEditing(false); setValue(initial); save.setError(null); clear.setError(null); }}>{C.cancel}</Button>
      </div>
      {(save.error ?? clear.error) && <p role="alert" className="text-xs text-ks-warn">{save.error ?? clear.error}</p>}
    </div>
  );
}

function NewThresholdCell({ row, areaName, params }: { row: ShippingRow; areaName: string; params: ShippingParams }) {
  const d = row.definition!;
  const staged = row.staged;
  const savedValue = staged?.new_threshold ?? "";
  const [value, setValue] = useState(savedValue);
  // The typed value becomes the saved one as C2 wrote it ("45" reads back "45.00").
  // Either write's success clears both sentences, so a stale refusal never blocks the sync below (review R2-2).
  const save = useShippingWrite((v: string) => stageThreshold(d.country, v), areaName, "save", (line) => { setValue(line.new_threshold ?? ""); discard.setError(null); });
  const discard = useShippingWrite((_: null) => unstageThreshold(d.country), areaName, "take_back", () => { setValue(""); save.setError(null); });
  // The cell is keyed by country, not by the saved line, so a refusal's refetch never wipes the typed value or its sentence;
  // the input follows a saved line that changes under it (a take-back or a commit elsewhere) only when nothing is refused.
  const [seen, setSeen] = useState(savedValue);
  if (seen !== savedValue) {
    setSeen(savedValue);
    if (!save.error && !discard.error) setValue(savedValue);
  }
  const pending = thresholdPending(value, staged ? staged.new_threshold : null);
  const submit = () => {
    const v = cleanAmount(value);
    if (!v) { save.setError(C.enterAmount); return; }
    save.mutation.mutate(v);
  };
  const busy = save.mutation.isPending || discard.mutation.isPending;
  const candidate = replayCandidate(useDebounced(value, 400), d.threshold);   // asked once the typing rests, as the projection
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <Input className="h-8 w-24" inputMode="decimal" placeholder={d.threshold ?? ""} aria-label={C.thresholdLabel(countryName(d.country))} value={value} onChange={(e) => setValue(e.target.value)} />
        <span className="text-xs text-ks-grey-1">{d.currency}</span>
      </div>
      <div className="flex gap-1">
        {pending && value !== "" && <Button size="sm" disabled={busy} onClick={submit}>{C.save}</Button>}
        {/* reset only drops the typing; the saved line is removed by its own action (review ruling 1). */}
        {pending && <Button size="sm" variant="outline" disabled={busy} onClick={() => { setValue(savedValue); save.setError(null); discard.setError(null); }}>{C.reset}</Button>}
        {staged && !pending && <Button size="sm" variant="outline" disabled={busy} onClick={() => discard.mutation.mutate(null)}>{C.discard}</Button>}
      </div>
      {staged && !pending && <span className="text-xs font-semibold text-ks-warn">{staged.new_threshold == null ? C.savedRemoval : C.savedNotLive}</span>}
      {(save.error ?? discard.error) && <p role="alert" className="text-xs text-ks-warn">{save.error ?? discard.error}</p>}
      {candidate && <ReplayLine country={d.country} currency={d.currency} candidate={candidate} current={d.threshold} params={params} areaName={areaName} />}
    </div>
  );
}

/** The batch bar and the commit dialog: shown while any threshold is saved; commits the runs of the lines it lists. */
function ThresholdBatchBar({ s, params, queryKey, areaName, onCommitted }: {
  s: Shipping; params: ShippingParams; queryKey: readonly unknown[]; areaName: string; onCommitted: (runs: CommittedRun[]) => void;
}) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unchanged, setUnchanged] = useState<UnchangedLine[]>([]);
  const [checking, setChecking] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  // No commit while a save or take-back is in flight: the commit would go out on the runs before it lands.
  const writing = useIsMutating({ mutationKey: WRITE_KEY }) > 0;
  const saved = savedRows(s);
  const commit = useMutation({
    mutationFn: () => commitThresholds(commitRunIds(s), commitExpected(s)),
    onSuccess: (data) => {
      void client.invalidateQueries({ queryKey: SHIPPING_KEY });
      // The Logs tab lists the Free-shipping threshold entries this commit just wrote.
      void client.invalidateQueries({ queryKey: LOGS_KEY });
      setError(null); setUnchanged([]); setOpen(false); onCommitted(data.committed);
    },
    // Refused as a whole, nothing is committed; the dialog stays open and names each line already at its threshold, or
    // (C1 v1.38) each threshold saved, saved again or taken back since it opened, from the list it showed (`saved`).
    onError: (e) => { setError(reviewChangedSentence(e, saved) ?? shippingRefusal(e, areaName, "commit")); setUnchanged(unchangedLines(e)); void client.invalidateQueries({ queryKey: SHIPPING_KEY }); },
  });
  // Open on a fresh read, so the dialog lists what is saved now and commits exactly those lines' runs (review ruling A1).
  // A failed refetch opens nothing and says why (review R2-3): the dialog is never opened on a read it could not refresh.
  const review = async () => {
    setChecking(true); setReviewError(null);
    await client.refetchQueries({ queryKey });
    const state = client.getQueryState(queryKey);
    setChecking(false);
    if (state?.status === "error") setReviewError(readSentence(state.error, areaName));
    else setOpen(true);
  };
  // Kept mounted while the dialog is open, so a refusal whose refetch empties the list still shows its sentence.
  if (saved.length === 0 && !open) return null;
  const orders = C.orderType[params.orders];
  const net = s.window.from && s.window.to ? C.modalNet(`${humanDate(s.window.from)} to ${humanDate(s.window.to)}`, orders) : C.modalNetNone;
  return (
    <section role="region" aria-label={C.batch(saved.length)} className="flex flex-wrap items-center gap-3 rounded-md border border-ks-warn bg-ks-warn/5 p-3">
      <span className="font-semibold text-ks-ink">{C.batch(saved.length)}</span>
      <Button size="sm" disabled={writing || checking} onClick={() => void review()}>{checking ? C.checking : C.review}</Button>
      {reviewError && <p role="alert" className="text-sm text-ks-warn">{reviewError}</p>}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setError(null); setUnchanged([]); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{C.modalTitle(saved.length)}</DialogTitle>
            <DialogDescription>{C.modalNote}</DialogDescription>
          </DialogHeader>
          {saved.length === 0 ? <p className="text-sm text-ks-ink">{C.nothingSaved}</p> : <ThresholdCommitTable rows={saved} netLabel={net} />}
          {error && <CommitRefusal error={error} lines={unchanged} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{C.cancel}</Button>
            {saved.length > 0 && <Button disabled={commit.isPending || writing} onClick={() => commit.mutate()}>{commit.isPending ? C.committing : C.commitButton(saved.length)}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/** A failed read as a sentence: a busy store (503) is offered as a retry, anything else as unreachable or forbidden. */
function readSentence(error: unknown, areaName: string): string {
  return isBusy(error) ? C.busyRead : messageFor(error, areaName);
}

/** A refused commit: C2's sentence, then each line already at its threshold (409 `unchanged_at_commit`). */
export function CommitRefusal({ error, lines }: { error: string; lines: UnchangedLine[] }) {
  return (
    <div role="alert" className="text-sm text-ks-warn">
      <p>{error}</p>
      {lines.map((l) => <p key={l.line_id}>{C.unchanged(countryName(l.country), l.committed_threshold == null ? C.noTier : money(l.committed_threshold, l.currency))}</p>)}
    </div>
  );
}

/** What the commit dialog lists: each saved threshold with the current one and the period's shipping net for its country. */
export function ThresholdCommitTable({ rows, netLabel }: { rows: ShippingRow[]; netLabel: string }) {
  return (
    <Table>
      <TableHeader><TableRow>{[...C.modalColumns, netLabel].map((c, i) => <TableHead key={c} className={i > 0 ? "text-right" : undefined}>{c}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.store}|${row.country ?? ""}`}>
            <TableCell><b>{countryName(row.country)}</b> <span className="text-xs text-ks-grey-1">{row.country}</span></TableCell>
            <TableCell className="text-right tabular-nums">{row.definition?.threshold == null ? C.noTier : money(row.definition.threshold, row.currency)}</TableCell>
            <TableCell className="text-right font-semibold tabular-nums">{row.staged?.new_threshold == null ? C.noTier : money(row.staged.new_threshold, row.currency)}</TableCell>
            <TableCell className="text-right tabular-nums">{row.shipping_net == null ? money(null, row.currency) : signedMoney(row.shipping_net, row.currency)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** The list to type into the Shopify admin after a commit: one row per committed threshold, ticked as the person goes. */
/** `fresh` is how many of the runs, first in the list, are the commit's own answer; the rest are recent last-committed runs. */
export function ThresholdApplyPanel({ committed, fresh, onDone }: { committed: CommittedRun[]; fresh: number; onDone: () => void }) {
  const lines = committed.flatMap((run) => run.lines.map((line) => ({ line, run })));
  const [done, setDone] = useState<ReadonlySet<number>>(new Set());
  const first = committed[0];
  const freshLines = committed.slice(0, fresh).reduce((n, run) => n + run.lines.length, 0);
  const toggle = (id: number) => setDone((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  return (
    <section role="region" aria-label={C.applyRegion} className="rounded-md border border-ks-purple bg-ks-purple-4 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-ks-ink">
          <p><b>{fresh ? C.committedTitle(freshLines, stamp(first?.committed_at), personName(first?.committed_by)) : C.lastCommittedTitle}</b> {C.applyNote}</p>
          {committed.slice(fresh).map((run) => <p key={run.id} className="text-xs text-ks-grey-1">{C.runLine(STORE_LABEL[run.store] ?? run.store, stamp(run.committed_at), personName(run.committed_by))}</p>)}
          <p className="text-xs text-ks-warn">{C.zoneNote}</p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onDone}>{C.done}</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead><span className="sr-only">{C.applied}</span></TableHead>{C.applyColumns.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {lines.map(({ line, run }) => (
            <TableRow key={line.id} className={done.has(line.id) ? "text-ks-grey-1" : undefined}>
              <TableCell><Checkbox aria-label={C.applied} checked={done.has(line.id)} onCheckedChange={() => toggle(line.id)} /></TableCell>
              <TableCell>{STORE_LABEL[run.store] ?? run.store}</TableCell>
              <TableCell>{countryName(line.country)} <span className="text-xs text-ks-grey-1">{line.country}</span></TableCell>
              <TableCell className="tabular-nums">
                {line.old_threshold == null ? C.noTier : money(line.old_threshold, line.currency)} → <b>{line.new_threshold == null ? C.noTier : money(line.new_threshold, line.currency)}</b>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}
